import { Routes, Route, useSearchParams } from "react-router-dom";
import Main from "./pages/main";
import Login from "./pages/login";
import Registration from "./pages/registration";
import Verify from "./pages/verify";
import TestYourself from './pages/test-yourself';
import Header from "./components/Header";
import Categories from "./pages/categories";
import Payment from "./pages/payment";
import Modal from "./modals";
import Footer from "./components/Footer";
import UsageConsiderations from "./pages/usage-considerations";
import { getUserInfo } from "./api/auth";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./redux/me.slice";
import { setAmbassador } from "./redux/ambassador.slice";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useCategoriesQuery } from "./api/mutations/categories.query";
import Loader from "./components/Loader";
import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { getAmbassadorMeReq } from "./api/ambassador";
import { getReferralLinkReq } from "./api/invitation";
import { setInvitation } from "./redux/invitation.slice";
import TopBarBanner from "./components/TopBarBanner";

const hasFreeMonthFromInvites = (user) => {
  const invitedUsers = user?.invites?.invited_users ?? 0;
  const freeSubscriptionUsed = user?.invites?.free_subscription_used ?? 0;

  return invitedUsers > freeSubscriptionUsed;
};

const getAvailableInviteFreeMonths = (user) => {
  const invitedUsers = user?.invites?.invited_users ?? 0;
  const freeSubscriptionUsed = user?.invites?.free_subscription_used ?? 0;

  return Math.max(invitedUsers - freeSubscriptionUsed, 0);
};

export const useUserInfoQuery = () => {
  const dispatch = useDispatch();

  const { isLoading } = useQuery({
    queryKey: ["user-info"],
    queryFn: async () => {
      try {
        const token = localStorage.getItem("assessmentai-token");
        if (!token) {
          throw { token: false };
        }

        const response = await getUserInfo();
        const userData = response.data;
        dispatch(setUser(userData));

        return true;
      } catch (error) {
        return false;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  return { isLoading };
};

export const useAmbassadorMeQuery = () => {
    const dispatch = useDispatch();

    const { isLoading } = useQuery({
      queryKey: ["get-ambassador-me"],
      queryFn: async () => {
        try {
          const token = localStorage.getItem("assessmentai-token");
          if (!token) {
            throw { token: false };
          }

          const response = await getAmbassadorMeReq();
          const ambassadorData = response.data;
          dispatch(setAmbassador(ambassadorData));

          return true;
        } catch (error) {
          dispatch(setAmbassador(null));
          return false;
        }
      },
      retry: false,
      refetchOnWindowFocus: false,
    });

    return { isLoading };
};

export const useGetReferralLinkQuery = () => {
    const dispatch = useDispatch();

    const { isLoading } = useQuery({
      queryKey: ["get-referral-link"],
      queryFn: async () => {
        try {
          const token = localStorage.getItem("assessmentai-token");
          if (!token) {
            throw { token: false };
          }

          const response = await getReferralLinkReq();
          const referralLinkData = response.data;
          dispatch(setInvitation(referralLinkData));

          return true;
        } catch (error) {
          dispatch(setInvitation(null));
          return false;
        }
      },
      retry: false,
      refetchOnWindowFocus: false,
    });

    return { isLoading };
};

const ProtectedRoute = ({ user, children }) => {
  const params = new URLSearchParams(window.location.search);
  const pathName = window.location.pathname;
  const testYourselfId = params.get("test-yourself");

  if (!user) {
    if (testYourselfId) {
      return <Navigate to={`/login?test-yourself=${testYourselfId}`} replace />;
    } else if (pathName === "/payment") {
      return <Navigate to="/login?payment=true" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }
  return children;
};

const ReferralRedirect = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    localStorage.removeItem('assessmentai-token');
    dispatch(setUser(null));
    navigate(`/registration?slug=${slug}`, { replace: true });
  }, []);

  return null;
};

const InviteRedirect = () => {
  const { referral_code } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    localStorage.removeItem('assessmentai-token');
    dispatch(setUser(null));
    navigate(`/registration?referral_code=${referral_code}`, { replace: true });
  }, []);

  return null;
};

const PublicRoute = ({ user, children }) => {
  const params = new URLSearchParams(window.location.search);
  const pathName = window.location.pathname;
  const testYourselfId = params.get("test-yourself");
  const isPayment = params.get("payment");

  if (user) {
    if (testYourselfId) {
      return <Navigate to={`/?test-yourself=${testYourselfId}`} replace />;
    } else if (pathName === "/verify" || isPayment) {
      return <Navigate to="/payment" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }
  return children;
};

const getTopBarBannerText = (user) => {
  if (!user) return null;

  const premiumStatus = user?.user_premium_status?.status;
  const isStatusNull = premiumStatus == null || premiumStatus === "null";

  if (hasFreeMonthFromInvites(user)) {
    const availableFreeMonths = getAvailableInviteFreeMonths(user);
    const monthLabel = availableFreeMonths === 1 ? "month" : "months";

    if (isStatusNull) {
      return `Referral reward unlocked ✓ You currently have ${availableFreeMonths} free ${monthLabel} available from your invites. Please proceed with the monthly subscription plan and your free ${monthLabel} will be applied automatically.`;
    }

    return `Referral reward unlocked ✓ You currently have ${availableFreeMonths} free ${monthLabel} available from your invites.`;
  }

  if (user?.has_referral_discount) {
    if (isStatusNull) {
      return "Welcome! 🎊 Because you joined through a referral invitation, you've received 1 month of free subscription access. Please proceed with purchasing the monthly subscription plan - it will be free for you.";
    }

    return "Welcome! 🎊 Because you joined through a referral invitation, you've received 1 month of free subscription access.";
  }

  return null;
};

function App() {
  const location = useLocation();
  const modalIds = useSelector((state) => state.modal.modalIds);
  const modalData = useSelector((state) => state.modal.modalData);
  const currentModalIndex = useSelector(
    (state) => state.modal.currentModalIndex
  );
  const user = useSelector((state) => state.me.data);
  const { isLoading } = useUserInfoQuery();
  const { isLoading: isAmbassadorLoading } = useAmbassadorMeQuery();
  const { isLoading: isReferralLinkLoading } = useGetReferralLinkQuery();
  const [searchParams] = useSearchParams();
  const topBarBannerText = getTopBarBannerText(user);

  useLayoutEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      localStorage.setItem("assessmentai-token", token);
    }
  }, []);
  useCategoriesQuery();

  if (isLoading) return <Loader />;

  return (
    <>
      {topBarBannerText && <TopBarBanner text={topBarBannerText} />}
      {location.pathname !== '/test/' &&
        <Header />
      }
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute user={user}>
              <Main />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/:slug"
          element={
            <ProtectedRoute user={user}>
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment"
          element={
            <ProtectedRoute user={user}>
              <Payment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute user={user}>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/registration"
          element={
            <PublicRoute user={user}>
              <Registration />
            </PublicRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <PublicRoute user={user}>
              <Verify />
            </PublicRoute>
          }
        />
        <Route
          path="/test"
          element={
            <ProtectedRoute user={user}>
              <TestYourself />
            </ProtectedRoute>
          }
        />
        <Route
          path="/r/:slug"
          element={<ReferralRedirect />}
        />
        <Route
          path="/invite/:referral_code"
          element={<InviteRedirect />}
        />
        <Route
          path="/usage-considerations"
          element={<UsageConsiderations />}
        />
      </Routes>

      {location.pathname !== '/test/' &&
        <Footer />
      }

      {modalIds?.length > 0 && modalIds[currentModalIndex] && (
        <Modal
          modalId={modalIds[currentModalIndex]}
          modalData={modalData[currentModalIndex]}
        />
      )}
    </>
  );
}

export default App;
